import { Typography } from '../../components/typography/typography';
import { Button } from '../../components/form/button';
import React, { Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { TaxRateModal } from './tax-rate-modal';
import classNames from 'classnames';
import { Card } from '../../components/card/card';
import Icon from '../../components/icons/icon';
import { SearchTaxCodesRequest, TaxCode } from '../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../clients/quipli-service/tax/tax-api-v1';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { Paginator } from '../../components/paginator/paginator-v2';
import { Pagination } from '../../clients/quipli-service/pagination/pagination';
import { SearchBar } from '../../components/search-bar/search-bar';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { TenantContext } from '../../context/tenant-context';
import { Dropdown } from '../../components/dropdown/dropdown';
import { Action } from '../../components/actions/actions';
import { Tooltip } from '../../components/tooltip/tooltip';
import { Modal } from '../../components/modal/modal';

function extractTaxCodeSearchParams(params: URLSearchParams): SearchTaxCodesRequest {
  return {
    taxCodeName: params.has('keyword') ? params.get('keyword') : undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
  } as SearchTaxCodesRequest;
}

const Filters = ({
  request,
  setRequest,
}: {
  request: SearchTaxCodesRequest;
  setRequest: Dispatch<SetStateAction<SearchTaxCodesRequest>>;
}) => {
  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <div className={classNames('relative', 'block', 'filter')}>
          <Dropdown
            trigger={(isOpen) => (
              <Button className="button-dropdown">
                <Typography type="text-sm" className="text-quipli-black-800">
                  QBO Assignment Status
                </Typography>
                {isOpen ? <Icon icon="ChevronUp" size="small" /> : <Icon icon="ChevronDown" size="small" />}
              </Button>
            )}
          >
            <div>
              <div
                className="flex gap-2"
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, hasQBOId: true }));
                }}
              >
                <Typography type="text-sm">Assigned</Typography>
              </div>
            </div>
            <div>
              <div
                className="flex gap-2"
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, hasQBOId: false }));
                }}
              >
                <Typography type="text-sm">Unassigned</Typography>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>

      {request.hasQBOId !== undefined && (
        <div className="pb-2 flex flex-col space-y-2">
          <div className="flex space-x-2">
            <div className="badge flex items-center space-x-1 px-2 bg-gray-200 text-gray-800 rounded-full">
              <Typography type="text-sm" className="text-quipli-grey-700">
                {request.hasQBOId == true ? 'Assigned' : 'Unassigned'}
              </Typography>
              <Action
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, hasQBOId: undefined }));
                }}
                icon={<Icon size="small" icon="X" />}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export const TaxRateSetup = () => {
  const { searchTaxCodes, deleteTaxCode } = useTaxApiV1();
  const { configuration } = useContext(TenantContext);
  const [setupModalOpen, setSetupModalOpen] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<ReactNode>();
  const [taxRates, setTaxRates] = useState<Pagination<TaxCode>>();
  const [expandedTaxRate, setExpandedTaxRate] = useState<string>();
  const [taxCodeToEdit, setTaxCodeToEdit] = useState<TaxCode | undefined>();
  const [params] = useSearchParams();
  const { request: searchRequest, setRequest: setSearchRequest } = useRequestQueryParams<SearchTaxCodesRequest>(
    extractTaxCodeSearchParams(params),
    () => init(),
  );

  let qboEnabled = false;
  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }
  // @hipaa-exclude-end

  async function init() {
    const taxRateResult = await searchTaxCodes(searchRequest);
    setTaxRates(taxRateResult);
  }

  useEffect(() => {
    init();
  }, []);
  return (
    <div className="tax-code-setup">
      <div className="pt-10 pb-5 flex items-center justify-between">
        <SearchBar
          data-cy="search-tax-code"
          placeholder="Search For Tax Code"
          defaultValue={searchRequest.keyword}
          setRequest={setSearchRequest}
        />
      </div>
      <div className="flex items-center justify-between">
        {qboEnabled ? <Filters request={searchRequest} setRequest={setSearchRequest} /> : <div></div>}
        <Button
          className="button-yellow"
          onClick={() => {
            setSetupModalOpen(true);
            setTaxCodeToEdit(undefined);
          }}
        >
          Create a tax code
        </Button>
      </div>
      <div className={'tax-code-table py-2 pt-8 pb-8'}>
        <Card>
          <table className="w-full border-none">
            <thead>
              <tr>
                <th className={'text-left'}>
                  <span>Name</span>
                </th>
                <th className={'text-right'}>
                  <span>Total Percentage</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {taxRates ? (
                taxRates.items.map((code) => {
                  return (
                    <React.Fragment key={code.id}>
                      <tr
                        className={classNames('tax-code-row', {
                          'expanded bg-grey-100 border-none': expandedTaxRate === code.id,
                          'bg-red-100': !code.qboTaxCodeId && qboEnabled,
                        })}
                        onClick={() =>
                          setExpandedTaxRate((expandedTaxCode) => (expandedTaxCode === code.id ? undefined : code.id))
                        }
                      >
                        <td className={'text-left gap-2 items-center mt-2.5'}>
                          <div className={'tax-name group flex flex-wrap gap-2 items-start h-16'}>
                            <span className={'self-stretch'}>{code.name}</span>
                            <div className={'flex flex-wrap flex items-start'}>
                              <i
                                className={classNames('fa text-[var(--grey-500)]', {
                                  'fa-angle-up': expandedTaxRate === code.id,
                                  'fa-angle-down': expandedTaxRate !== code.id,
                                })}
                              />
                              <div className="taxcode-actions hidden group-hover:block self-start max-h-full">
                                <div className="flex flex-row">
                                  <Tooltip
                                    type={'black'}
                                    tooltip={
                                      qboEnabled && !code.qboTaxCodeId ? 'QBO Tax Agency not assigned' : 'Edit Tax Code'
                                    }
                                  >
                                    <Button
                                      className="button-tertiary taxcode-button max-h-fit"
                                      onClick={() => {
                                        setTaxCodeToEdit(code);
                                        setSetupModalOpen(true);
                                      }}
                                    >
                                      <Icon icon={'Edit'} stroke="var(--grey-400)" />
                                    </Button>
                                  </Tooltip>
                                  <Button
                                    className="button-tertiary taxcode-button max-h-fit"
                                    onClick={() => {
                                      setConfirmModal(
                                        <ConfirmModal
                                          isOpen={true}
                                          prompt="Delete Tax Code"
                                          headerIcon={
                                            <div className="flex justify-center">
                                              <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                                            </div>
                                          }
                                          additionalText={
                                            <div className="text-center px-4">
                                              <Typography type="text-sm" className="text-grey-500">
                                                Are you sure you want to delete this tax code?
                                                <br />
                                                {`${code.name} will be permanently deleted.`}
                                              </Typography>
                                            </div>
                                          }
                                          yesButtonYellow
                                          yesLabel="Delete"
                                          noLabel="Cancel"
                                          onConfirm={() => (deleteTaxCode(code.id), setConfirmModal(undefined))}
                                          close={() => setConfirmModal(undefined)}
                                        />,
                                      );
                                    }}
                                  >
                                    <Icon icon={'X'} stroke="var(--grey-400)" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={'text-right'}>{code.totalPercentage}%</td>
                      </tr>
                      {code.id === expandedTaxRate && (
                        <tr
                          className={classNames('tax-code-list expanded:bg-grey-100 expanded:border-none', {
                            'expanded bg-grey-100 border-none': expandedTaxRate === code.id,
                          })}
                        >
                          <td colSpan={100}>
                            <div className="p-2 pt-0 flex flex-column gap-2">
                              <Card className="w-full">
                                <div className="pb-[2px]">
                                  <Typography type={'text-md'} weight={'medium'}>
                                    Jurisdictions
                                  </Typography>
                                </div>
                                <div className={'gap-4  py-2'}>
                                  <table className="w-full gap-3">
                                    <thead>
                                      <tr>
                                        <th className="text-left">Name</th>
                                        <th className="text-right">Percentage</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {code.jurisdictions.map((j) => {
                                        return (
                                          <tr key={j.id}>
                                            <td className="text-left">{j.name}</td>
                                            <td className="text-right">{j.percentage}%</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </Card>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <> </>
              )}
            </tbody>
          </table>
          <Paginator
            pagination={taxRates?.pagination}
            request={searchRequest}
            setRequest={(pag) =>
              setSearchRequest((req) => {
                return { ...req, ...pag };
              })
            }
          />
        </Card>
      </div>
      <TaxRateModal
        isOpen={setupModalOpen}
        close={(qboChanged: boolean) => {
          setSetupModalOpen(false);
          if (qboChanged) {
            setConfirmModal(
              <Modal
                isOpen={true}
                headerIcon={
                  <div className="flex justify-center">
                    <Icon icon="InfoCircle" size="xlarge" stroke="var(--grey-500)" />
                  </div>
                }
                additionalText={
                  <div className="text-center px-4">
                    <Typography type="text-sm" className="text-grey-500">
                      Tax code updated and related orders re-synced to QBO.
                    </Typography>
                  </div>
                }
                close={() => setConfirmModal(undefined)}
              />,
            );
          }
          setTaxCodeToEdit(undefined);
          init();
        }}
        taxCode={taxCodeToEdit}
      ></TaxRateModal>
      {confirmModal}
    </div>
  );
};
